
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { observable, action, reaction, toJS } from 'mobx'
import { getFromApi } from '../services/apiService'
import moment, { Moment } from 'moment-timezone'
import { IStores } from '.'
import { sendBookingAnalyticEvent } from '../services/analyticsService'
import 'moment-business-time'
import {
  AllSchedule, DCLSchedule, ITimeOption, NoSchedule, generateTimeOptions, getDayScheduleFor, getFirstWorkingDateTime,
  getLastWorkingDateTime
} from '../utils/workingTime'

import { ILocationSchedule } from './LocationStore'

const API_DATE_FORMAT = 'YYYY-MM-DDTHH:mm'

const build_CARCALENDAR_URL = (locationReference: string) =>
  `/v3/reserve/carsCalendar/${locationReference}`

interface ICalendarDay {
  calendarDay: string
  available: boolean
  message: string
  color: string
  isDefault: boolean
}

class CalendarStore {
  store: IStores

  @observable loading = false
  @observable schedules: ILocationSchedule = NoSchedule
  @observable startTimeOptions: ITimeOption[] = []
  @observable endTimeOptions: ITimeOption[] = []
  @observable unavailableDays: Date[] = []
  @observable calendarMap = new Map<Moment, ICalendarDay>()
  @observable fromMonth: Moment = moment().startOf('month')
  @observable month: Moment = moment().startOf('month')
  @observable firstFreeDateTime: Moment | null = null
  @observable selectedStartAt: Moment | null = null
  @observable selectedEndAt: Moment | null = null

  constructor(store: IStores) {
    this.store = store

    reaction(
      () => {
        const selectedProduct = this.store.productsStore.selectedProduct
        const selectedLocation = this.store.locationsStore.selectedLocation

        return {
          productReference: selectedProduct?.reference,
          productScheduleUnit: selectedProduct?.scheduleUnit,
          productMinimumLeadTimeInScheduleUnit: selectedProduct?.minimumLeadTimeInScheduleUnit || 0,
          productMaximumLeadTimeInScheduleUnit: selectedProduct?.maximumLeadTimeInScheduleUnit || 0,
          productDurationUnit: selectedProduct?.durationUnit,
          locationPickupSchedule: selectedLocation?.pickupSchedule,
          locationTimezone: selectedLocation?.timezone,
          selectedStartDate: this.selectedStartAt?.format(API_DATE_FORMAT),
          selectedEndDate: this.selectedEndAt?.format(API_DATE_FORMAT)
        }
      },
      data => {
        if (!data.productScheduleUnit || !data.locationPickupSchedule ) {
          return
        }

        const locationSchedules = data.locationPickupSchedule
        console.debug('CalendarStore.reaction - update timings for ', data)
        const InsuranceValidationSchedules = data.productReference === 'WEEKLY_SUBSCRIPTION_UK' ? DCLSchedule || AllSchedule : AllSchedule
        const productMinimumLeadInMinutes = data.productScheduleUnit * data.productMinimumLeadTimeInScheduleUnit
        const minimumLeadTimeInInHours = moment.duration(productMinimumLeadInMinutes, 'minutes').asHours()
        const insuranceFirstDateTime = getFirstWorkingDateTime(
          moment().tz(data.locationTimezone || 'UTC'),
          InsuranceValidationSchedules,
          minimumLeadTimeInInHours
        )
        console.debug('CalendarStore.reaction - update timings - compute insuranceFirstDateTime ', insuranceFirstDateTime.format(), toJS(locationSchedules))
        const _firstFreeDateTime = getFirstWorkingDateTime(
          insuranceFirstDateTime,
          toJS(locationSchedules),
          0)
        console.debug('CalendarStore.reaction - update timings - compute firstFreeDateTime ', _firstFreeDateTime.format())

        const productMaximumLeadTimeInScheduleUnit = data.productScheduleUnit *
        data.productMaximumLeadTimeInScheduleUnit
        const maximumLeadTimeInInHours = moment.duration(productMaximumLeadTimeInScheduleUnit, 'minutes').asHours()
        const _lastFreeDateTime = getLastWorkingDateTime(moment().tz(data.locationTimezone || 'UTC'), InsuranceValidationSchedules, maximumLeadTimeInInHours)

        if (data.selectedStartDate) {
          const selectedStartDate = moment(data.selectedStartDate)
          const daySchedule = getDayScheduleFor(selectedStartDate, locationSchedules)
          const options = generateTimeOptions(data.productScheduleUnit, daySchedule)
          this.startTimeOptions = options

          if (selectedStartDate.isBefore(_firstFreeDateTime)) {
            this.selectStartAt(_firstFreeDateTime.clone().add(1, 'hours').startOf('hours'))
          } else if (productMaximumLeadTimeInScheduleUnit && selectedStartDate.isAfter(_lastFreeDateTime)) {
            this.selectStartAt(_lastFreeDateTime.clone().subtract(1, 'hours').startOf('hours'))
          }

          console.debug('CalendarStore.reaction - update timings - compute start minTime maxTime', selectedStartDate.format('dddd'), daySchedule, options )
        }

        if (data.selectedEndDate) {
          const selectedEndDate = moment(data.selectedEndDate)
          const daySchedule = getDayScheduleFor(selectedEndDate, locationSchedules)
          const options = generateTimeOptions(data.productScheduleUnit, daySchedule)
          this.endTimeOptions = options

          console.debug('CalendarStore.reaction - update timings - compute end minTime maxTime', selectedEndDate.format('dddd'), daySchedule, options )
        }

        this.firstFreeDateTime = _firstFreeDateTime.clone().startOf('hours')
        this.schedules = locationSchedules

        console.debug('update schedule with ', toJS(locationSchedules) )
      },
      { fireImmediately: true }
    )
  }

  @action list = async (locationReference: string) => {
    this.store.bookingStore.loading = true

    const response = await getFromApi(build_CARCALENDAR_URL(locationReference))

    const calendarDays: ICalendarDay[] =
      response && response.status === 200 ? response.data.calendarDays : []

    for (const calendarDay of calendarDays) {
      const day = moment(calendarDay.calendarDay)

      if (calendarDay.available === false) {
        this.unavailableDays.push(
          day
            .clone()
            .startOf('day')
            .toDate()
        )
      }
      this.calendarMap.set(day, calendarDay)
    }
    this.store.bookingStore.loading = false
  }

  @action selectStartAt = async (startAt: Moment | null) => {
    this.loading = true

    if (!startAt) {
      this.selectedStartAt = null
      this.loading = false

      return
    }

    if (this.selectedStartAt?.isSame(startAt)) {
      this.loading = false

      return
    }

    this.selectedStartAt = startAt.clone()

    this.loading = false
  }

  @action selectEndAt = async (endAt: Moment | null) => {
    this.loading = true

    if (!endAt) {
      this.selectedEndAt = null
      this.loading = false

      return
    }

    if (this.selectedEndAt?.isSame(endAt)) {
      this.loading = false

      return
    }

    if (this.store.productsStore.selectedProduct && this.selectedStartAt?.isSameOrAfter(endAt)) {
      const scheduleUnit = this.store.productsStore.selectedProduct.scheduleUnit

      this.selectedEndAt = endAt.clone().add(scheduleUnit, 'minutes')
    } else {
      this.selectedEndAt = endAt.clone()
    }

    sendBookingAnalyticEvent('book_step_1_select_dates', this.store.productsStore.selectedProduct, this.store.locationsStore.selectedCity, this.store.locationsStore.selectedLocation, this.selectedStartAt, endAt)
    this.loading = false
  }

  @action reset = async () => {
    this.loading = true
    this.selectedStartAt = null
    this.selectedEndAt = null
    this.loading = false
  }
}

export default CalendarStore
